import type { ComponentProps, RefObject } from 'react'
import { forwardRef, type ReactNode } from 'react'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'

import { cn } from '@/lib/utils'

import type { AnimatedIdsRef } from './chat-animated-item.atom'
import type { ChatInteractionHandlers } from './chat-interaction-handlers.interface'
import { ChatLoadMoreIndicator } from './chat-load-more-indicator.atom'
import type { ChatItem } from './chat-message.interface'
import { ChatVirtualListItem } from './chat-virtual-list-item.atom'

const SCROLL_BUTTON_THRESHOLD = 200

const ScrollSeekPlaceholder: React.FC<{ height: number }> = ({ height }) => (
  <div
    className="mx-auto my-2 max-w-3xl animate-pulse rounded-lg bg-muted/50 px-4"
    style={{ height: Math.max(height - 16, 40) }}
  />
)

const CustomScroller = forwardRef<HTMLDivElement, ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('scrollbar-thin', className)} {...props} />
  )
)
CustomScroller.displayName = 'CustomScroller'

/** Props shared across all rendered items in the virtual list. */
export interface ChatVirtuosoRenderItemConfig extends ChatInteractionHandlers {
  animatedIdsRef: AnimatedIdsRef
  isAnimationEnabled: boolean
}

export interface ChatVirtuosoWrapperProps {
  virtuosoRef: RefObject<VirtuosoHandle | null>
  itemList: ChatItem[]
  renderItemConfig: ChatVirtuosoRenderItemConfig
  handleAtTopStateChange: (atTop: boolean) => void
  handleAtBottomStateChange: (atBottom: boolean) => void
  handleFollowOutput: (isCurrentlyAtBottom: boolean) => 'smooth' | false
  firstItemIndex: number
  isFetchingMoreMessages?: boolean
}

export const ChatVirtuosoWrapper: React.FC<ChatVirtuosoWrapperProps> = ({
  virtuosoRef,
  itemList,
  renderItemConfig,
  handleAtTopStateChange,
  handleAtBottomStateChange,
  handleFollowOutput,
  firstItemIndex,
  isFetchingMoreMessages = false,
}): ReactNode => (
  <Virtuoso
    ref={virtuosoRef}
    data={itemList}
    style={{ height: '100%', width: '100%' }}
    followOutput={handleFollowOutput}
    atTopStateChange={handleAtTopStateChange}
    atBottomStateChange={handleAtBottomStateChange}
    atBottomThreshold={SCROLL_BUTTON_THRESHOLD}
    initialTopMostItemIndex={itemList.length > 0 ? itemList.length - 1 : 0}
    firstItemIndex={firstItemIndex}
    increaseViewportBy={{ top: 800, bottom: 800 }}
    overscan={{ main: 30, reverse: 30 }}
    defaultItemHeight={60}
    skipAnimationFrameInResizeObserver
    components={{
      ScrollSeekPlaceholder,
      Scroller: CustomScroller,
      Header: () => <ChatLoadMoreIndicator isVisible={isFetchingMoreMessages} />,
    }}
    itemContent={(_, item) => <ChatVirtualListItem item={item} {...renderItemConfig} />}
    computeItemKey={(_, item) => item.stableKey ?? item.id}
  />
)
